// [PATCH_ROCNI_UVOZ_PARTNER_V1] zavihek "Nov dobavitelj" -> skupna FURS maska
import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../lib/api';
import { AlertTriangle, Plus, Search } from 'lucide-react';
import { PartnerFursForm } from './PartnerFursForm';

const normDav = (v: any) => String(v || '').replace(/\D/g, '').slice(-8);

export function PartnerSelector({ companyId, onSelect, predlog, zacetniNacin, maxBrezIskanja }: { // [PATCH izdaja-prodajni-dok-ui v2] maxBrezIskanja
  companyId: string;
  maxBrezIskanja?: number; // [PATCH izdaja-prodajni-dok-ui v2] brez vnosa pokaži le prvih N (Izdaja: 5); privzeto 50 kot doslej
  onSelect: (partner: any) => void;
  predlog?: any;
  izvozProgram?: string; // ohranjeno zaradi zdruzljivosti klicnih mest
  zacetniNacin?: 'obstojeci' | 'nov'; // [PATCH rocni-vnos-fe v3]
}) {
  const [partnerji, setPartnerji] = useState<any[]>([]);
  const [q, setQ] = useState('');
  const [insolventne, setInsolventne] = useState<Set<string>>(new Set());
  // Nacin: 'obstojeci' (iskanje) | 'nov' (FURS maska)
  const [nacin, setNacin] = useState<'obstojeci' | 'nov'>(zacetniNacin === 'nov' ? 'nov' : 'obstojeci'); // [PATCH rocni-vnos-fe v3]

  // [PATCH partner-isci-server v1] streznisko iskanje (naziv/davcna/sifra) namesto seznama 5000; prazen niz -> prvih 50 kot prej
  const zapSt = React.useRef(0);
  useEffect(() => {
    if (!companyId) return;
    const s = q.trim();
    const moj = ++zapSt.current;
    const t = setTimeout(() => {
      api.get(`/sifranti/partnerji/${companyId}`, { params: s ? { q: s, limit: 50 } : { limit: 50 } })
        .then(r => { if (moj === zapSt.current) setPartnerji(r.data || []); })
        .catch(() => {});
    }, s ? 250 : 0);
    return () => clearTimeout(t);
  }, [q, companyId]);

  useEffect(() => {
    let zivo = true;
    api.get('/insolventnost', { params: { obseg: 'moji', company_id: companyId } })  // PATCH_INSOLVENTNOST_FE_V1
      .then((r: any) => {
        if (!zivo) return;
        const s = new Set<string>();
        (r.data?.postavke || []).forEach((p: any) => {
          const d = normDav(p.davcna);
          if (d) s.add(d);
        });
        setInsolventne(s);
      })
      .catch(() => {});
    return () => { zivo = false; };
  }, []);

  const filtered = useMemo(() => partnerji.slice(0, (!q.trim() && maxBrezIskanja) ? maxBrezIskanja : 50), [partnerji, q, maxBrezIskanja]); // [PATCH partner-isci-server v1] filtrira streznik // [PATCH izdaja-prodajni-dok-ui v2]

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Zavihka: obstojeci / nov */}
      <div className="flex gap-1 px-4 pt-3">
        <button onClick={() => setNacin('obstojeci')}
          className={'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ' +
            (nacin === 'obstojeci' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:bg-gray-800')}>
          <Search className="w-3.5 h-3.5" /> Obstoječi
        </button>
        <button onClick={() => setNacin('nov')}
          className={'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ' +
            (nacin === 'nov' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:bg-gray-800')}>
          <Plus className="w-3.5 h-3.5" /> Nov dobavitelj
        </button>
      </div>

      {nacin === 'obstojeci' ? (
        <>
          <div className="px-4 py-3 border-b border-gray-700">
            <input autoFocus value={q} onChange={e => setQ(e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm outline-none focus:border-blue-500"
              placeholder="Išči po imenu, davčni ali šifri..." />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.map(p => {
              const jeIns = insolventne.has(normDav(p.tax_number));
              return (
                <button key={p.id} onClick={() => onSelect(p)}
                  className="w-full text-left px-4 py-3 hover:bg-gray-800 transition border-b border-gray-800 last:border-0">
                  <div className="flex items-center gap-2">
                    {jeIns && (
                      <span title="V insolvenčnem postopku (AJPES)"
                        className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-300 bg-red-900/40 border border-red-700 px-1.5 py-0.5 rounded-full flex-shrink-0">
                        <AlertTriangle className="w-3 h-3" /> insolventen
                      </span>
                    )}
                    <span className={'text-sm font-medium ' + (jeIns ? 'text-red-200' : 'text-white')}>{p.name}</span>
                  </div>
                  <div className="text-xs text-gray-400 font-mono mt-0.5">{p.tax_number || '—'}</div>
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <div className="overflow-y-auto flex-1">
          {(predlog?.ime || predlog?.davcna) ? (
            <div className="mx-5 mt-3 text-xs text-gray-400 bg-gray-800/60 border border-gray-700 rounded-lg px-3 py-2">
              Predizpolnjeno z računa. Preveri (FURS/VIES) in po potrebi popravi pred shranjevanjem.
            </div>
          ) : null}
          {/* [PATCH_ROCNI_UVOZ_PARTNER_V1] skupna FURS maska (kot v Šifrantih) */}
          <PartnerFursForm companyId={companyId}
            initial={{
              name: predlog?.ime || '',
              tax_number: predlog?.davcna || '',
              vat_number: predlog?.vat || '',
              country: predlog?.drzava || 'SI',
              address: predlog?.naslov || '',
            }}
            onSaved={(p) => onSelect(p)}
            onCancel={() => setNacin('obstojeci')} />
        </div>
      )}
    </div>
  );
}
